import { Component, Input, ChangeDetectionStrategy } from '@angular/core';
import moment from 'moment';
import { FileExportService } from 'app/services/file-export.service';

import { FormsModule } from '@angular/forms';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatTooltipModule } from '@angular/material/tooltip';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { Action, ACTION_OPTIONS, ActivityLogFilters, Area, AREA_OPTIONS, IActivityLog } from 'app/models/activity-log.model';
import { ApiService } from 'app/services/api.service';
import { takeUntil } from 'rxjs/operators';
import { Subject } from 'rxjs';

@Component({
    selector: 'app-download-log',
    templateUrl: './download-log.component.html',
    styleUrls: ['./download-log.component.css'],
    changeDetection: ChangeDetectionStrategy.Default,
    imports: [
    FormsModule,
    MatExpansionModule,
    MatTooltipModule,
    TranslateModule
]
})
export class DownloadLogComponent {

  @Input() csvHeaders: string[] = [];
  @Input() showJson: boolean = true;

  @Input() startDate: string = '';
  @Input() endDate: string = '';
  @Input() startTime: string = '';
  @Input() endTime: string = '';

  @Input() selectedAreas: string[] = [];
  @Input() selectedActions: string[] = [];
  @Input() selectedUsers: string[] = [];
  @Input() search: string = '';

  loadingCSV = false;
  loadingJSON = false;

  private destroy$ = new Subject<void>();

  constructor(private fileExporter: FileExportService, private api: ApiService, private translate: TranslateService) {}

  private combineDateAndTime = (dateStr: string, timeStr: string) => {
    if (!dateStr) return null;
    if (!timeStr) timeStr = '00:00';
    return moment(dateStr).format('YYYY-MM-DD') + ' ' + timeStr;
  };

  fetchActivityLogsForDownload(): Promise<IActivityLog[]> {
    const filters: ActivityLogFilters = {
      reportTypes: [],
      users: this.selectedUsers,
      cameraGroups: [],
      areas: this.selectedAreas.map(a => a as Area),
      actions: this.selectedActions.map(a => a as Action),
      search: this.search,
      pageIndex: 0,
      pageSize: 1000,
      startDate: this.combineDateAndTime(this.startDate, this.startTime),
      endDate: this.combineDateAndTime(this.endDate, this.endTime),
    };

    return new Promise((resolve, reject) => {
      this.api.getActivityLogs(filters)
        .pipe(takeUntil(this.destroy$))
        .subscribe(resolve, reject);
    });
  }

  private getDisplayName<T extends { value: string; name: string }>(
    value: string,
    options: T[]
  ): string {
    const found = options.find(opt => opt.value === value);
    if (!found) return value;
    if (/^[A-Z0-9_]+$/.test(found.name)) {
      return this.translate.instant(found.name);
    }
    return found.name;
  }
  
  downloadCsvOrJson(exportType: "csv" | "json"): void {
    this.exportActivityLogsToCsvOrJson(exportType);
  }

  exportActivityLogsToCsvOrJson(exportType: "csv" | "json"): void {
    const filename = "activity-log-" + moment().format("YYYY-MM-DD");
    const headers = ["Timestamp", "Area", "User", "Action", "Details"];
    const options = {
      fieldSeparator: ";",
      quoteStrings: '"',
      decimalseparator: ".",
      showLabels: true,
      showTitle: false,
      headers: headers,
    };
  
    if (exportType === "csv") this.loadingCSV = true;
    if (exportType === "json") this.loadingJSON = true;
  
    this.fetchActivityLogsForDownload().then((logs: IActivityLog[]) => {
      if (!logs) return;
  
      switch (exportType) {
        case "csv":
          const csvLogs = logs.map((log) => ({
            Timestamp: moment.utc(log.timestamp).local().format("YYYY-MM-DD HH:mm:ss"),
            area: this.getDisplayName(log.area, AREA_OPTIONS),
            User: log.staffName,  
            action: this.getDisplayName(log.action, ACTION_OPTIONS),
            Details: log.details?.translationCode
              ? this.translate.instant(log.details.translationCode, {
                  targetName: log.details.targetName,
                  targetId: log.details.targetId
                })
              : '',
          }));
          this.fileExporter.exportActivityLogsToCSV(csvLogs, filename, options);
          this.loadingCSV = false;
          break;
  

        case "json":
          const jsonLogs = logs.map(log => {
            if (!log.details) {
              return {
                timestamp: log.timestamp,
                area: this.getDisplayName(log.area, AREA_OPTIONS),
                user: log.staffName,
                action: this.getDisplayName(log.action, ACTION_OPTIONS),
                details: ""
              };
            }

            const details = this.translate.instant(log.details.translationCode || "", {
              targetName: log.details.targetName,
              targetId: log.details.targetId,
            });

            const changeFields = [
              "accountChanges",
              "cameraGroupChanges",
              "scheduledVisitChanges",
              "alarmRouteChanges",
              "alarmUserGroupChanges"
            ];
        
            const changes: any = {};
            for (const field of changeFields) {
              if (log.details[field]) {
                changes[field] = log.details[field];
              }
            }
        
            return {
              timestamp: moment.utc(log.timestamp).local().format("YYYY-MM-DD HH:mm:ss"),
              area: this.getDisplayName(log.area, AREA_OPTIONS),
              user: log.staffName,
              action: this.getDisplayName(log.action, ACTION_OPTIONS),
              details: log.details?.translationCode
              ? this.translate.instant(log.details.translationCode, {
                  targetName: log.details.targetName,
                  targetId: log.details.targetId
                })
              : ''
            };
          });
        
          this.fileExporter.exportActivityLogsToJson(jsonLogs, filename);
          this.loadingJSON = false;
          break;
        }
      });
  }  
}